import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  ArrowsRightLeftIcon,
  GlobeAltIcon,
  MapPinIcon,
  BuildingOffice2Icon,
  ClockIcon,
  FunnelIcon,
  CursorArrowRaysIcon,
} from '@heroicons/react/24/outline';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useLanguage } from '../../contexts/LanguageContext';
import { fetchEngagementProduct } from '../../services/apiService';
import { formatMoneyCHF } from '../../utils/priceUtils';
import {
  Card,
  EmptyState,
  Delta,
  Kpi,
  RankedBars,
  CantonTileMap,
  WeekHourHeatmap,
  VIEWS_COLOR,
  VISITORS_COLOR,
  CARTS_COLOR,
  cantonName,
  countryName,
  flagEmoji,
  formatNum,
} from './engagementUi';

/**
 * Slide-over with everything about one product's engagement in the current
 * date range and filters: funnel (view → cart → order), daily trend, where
 * the clicks came from, and where the visitors are.
 */
export default function ProductEngagementDrawer({ pharmacode, filters, onClose, sourceLabel, dayLabels }) {
  const { t } = useLanguage();
  const tt = (k, r) => t(`statistics.engagement.${k}`, r);
  const unknown = t('statistics.countries.unknown');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!pharmacode) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(false);
    setData(null);
    fetchEngagementProduct(pharmacode, filters)
      .then((res) => !cancelled && setData(res?.data || null))
      .catch(() => !cancelled && setError(true))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [pharmacode, filters]);

  useEffect(() => {
    if (!pharmacode) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pharmacode, onClose]);

  const rows = useMemo(() => {
    if (!data) return null;
    return {
      sources: data.bySource.map((s) => ({ key: s.source, label: sourceLabel(s.source), value: s.views })),
      countries: data.byCountry.map((c) => ({
        key: c.country,
        label: countryName(c.country, unknown),
        prefix: c.country === 'Unknown' ? '🏳️' : flagEmoji(c.country),
        value: c.views,
      })),
      cantons: data.byRegion.map((r) => ({ key: r.region, label: cantonName(r.region, unknown), value: r.views })),
      cities: data.byCity.map((c) => ({ key: `${c.city}-${c.region}`, label: `${c.city}${c.region ? ` (${c.region})` : ''}`, value: c.views })),
    };
  }, [data, sourceLabel, unknown]);

  const p = data?.product;
  const totals = data?.totals;
  const vsPrev = t('statistics.vsPrev');

  // Visitor-level funnel: viewed → added to cart → paid orders.
  const funnel = totals
    ? [
        { label: tt('drawer.funnelViewed'), value: totals.visitors },
        { label: tt('drawer.funnelCarted'), value: totals.cartVisitors },
        { label: tt('drawer.funnelOrdered'), value: totals.purchases?.orders || 0 },
      ]
    : [];

  return ReactDOM.createPortal(
    <AnimatePresence>
      {pharmacode && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={tt('drawer.title')}>
          <motion.div
            className="absolute inset-0 bg-gray-900/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            className="absolute right-0 top-0 h-full w-full max-w-3xl bg-gray-50 shadow-2xl overflow-y-auto"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            <header className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">{tt('drawer.title')}</p>
                <h2 className="text-lg font-bold text-gray-900 truncate" title={p?.name || ''}>
                  {p?.name || (loading ? '…' : tt('unknownProduct'))}
                </h2>
                <p className="text-xs text-gray-500 flex items-center gap-2 flex-wrap mt-0.5">
                  <span className="tabular-nums">{pharmacode}</span>
                  {p?.brand && <span>· {p.brand}</span>}
                  {p?.basePrice != null && <span>· {formatMoneyCHF(p.basePrice)}</span>}
                  {p?.isBestseller && <span className="rounded bg-amber-50 text-amber-800 px-1.5 font-semibold">★ {tt('badges.bestseller')}</span>}
                  {p?.discount && (
                    <span className="rounded bg-rose-50 text-rose-700 px-1.5 font-semibold">
                      {p.discount.kind === 'percent' ? `−${p.discount.amount}%` : tt('badges.discount')}
                    </span>
                  )}
                  <Link
                    to={`/products/${pharmacode}`}
                    className="inline-flex items-center gap-0.5 font-semibold text-indigo-600 hover:text-indigo-700"
                  >
                    {tt('drawer.openProduct')}
                    <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                  </Link>
                </p>
              </div>
              <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label={t('statistics.listModal.close')}>
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </header>

            <div className="p-6 space-y-4">
              {loading && (
                <div className="flex justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                </div>
              )}
              {error && <div className="rounded-lg bg-red-50 text-red-700 text-sm px-4 py-3">{tt('error')}</div>}

              {data && rows && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Kpi label={tt('kpi.views')} value={formatNum(totals.views)} delta={<Delta value={data.deltas.views} newLabel={tt('new')} title={vsPrev} />} />
                    <Kpi label={tt('kpi.visitors')} value={formatNum(totals.visitors)} delta={<Delta value={data.deltas.visitors} newLabel={tt('new')} title={vsPrev} />} />
                    <Kpi
                      label={tt('table.cartRate')}
                      value={`${totals.cartRate.toFixed(1)}%`}
                      hint={tt('kpi.cartRateHint', { n: formatNum(totals.cartVisitors) })}
                      delta={<Delta value={data.deltas.cartRate} unit="pp" title={vsPrev} />}
                    />
                    <Kpi
                      label={tt('table.sold')}
                      value={formatNum(totals.purchases.units)}
                      hint={`${formatMoneyCHF(totals.purchases.revenue)} · ${tt('drawer.orders', { n: formatNum(totals.purchases.orders) })}`}
                    />
                  </div>

                  <Card title={tt('drawer.funnel')} icon={FunnelIcon}>
                    <div className="space-y-2">
                      {funnel.map((f, i) => {
                        const base = funnel[0].value || 1;
                        const prev = i === 0 ? null : funnel[i - 1].value;
                        return (
                          <div key={f.label} className="flex items-center gap-3">
                            <span className="w-40 shrink-0 text-xs text-gray-600">{f.label}</span>
                            <div className="flex-1 h-6 bg-gray-100 rounded-md overflow-hidden">
                              <div
                                className="h-full rounded-md flex items-center px-2"
                                style={{ width: `${Math.max((f.value / base) * 100, 3)}%`, backgroundColor: VIEWS_COLOR, opacity: 1 - i * 0.22 }}
                              >
                                <span className="text-[11px] font-semibold text-white tabular-nums">{formatNum(f.value)}</span>
                              </div>
                            </div>
                            <span className="w-14 shrink-0 text-right text-xs text-gray-500 tabular-nums">
                              {prev ? `${((f.value / prev) * 100).toFixed(1)}%` : ''}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-[10px] text-gray-400">{tt('drawer.funnelNote')}</p>
                  </Card>

                  <Card title={tt('charts.overTime')} icon={CursorArrowRaysIcon}>
                    {data.timeline.some((d) => d.views || d.carts) ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={data.timeline} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={(d) => d.slice(5)} minTickGap={24} />
                          <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                          <Tooltip formatter={(v) => formatNum(v)} labelStyle={{ fontWeight: 600 }} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Line type="monotone" dataKey="views" name={tt('kpi.views')} stroke={VIEWS_COLOR} strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="visitors" name={tt('kpi.visitors')} stroke={VISITORS_COLOR} strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="carts" name={tt('drawer.addToCart')} stroke={CARTS_COLOR} strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <EmptyState>{t('statistics.noData')}</EmptyState>
                    )}
                  </Card>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card title={tt('charts.sources')} icon={ArrowsRightLeftIcon}>
                      <RankedBars rows={rows.sources} emptyLabel={t('statistics.noData')} valueLabel={tt('unit')} />
                    </Card>
                    <Card title={tt('charts.countries')} icon={GlobeAltIcon}>
                      <RankedBars rows={rows.countries} emptyLabel={t('statistics.noData')} valueLabel={tt('unit')} />
                    </Card>
                    <Card title={tt('charts.cantons')} icon={MapPinIcon}>
                      {rows.cantons.length ? (
                        <>
                          <CantonTileMap rows={data.byRegion} unitLabel={tt('unit')} />
                          <div className="mt-4">
                            <RankedBars rows={rows.cantons} limit={5} valueLabel={tt('unit')} />
                          </div>
                        </>
                      ) : (
                        <EmptyState>{tt('noSwissData')}</EmptyState>
                      )}
                    </Card>
                    <Card title={tt('charts.cities')} icon={BuildingOffice2Icon}>
                      <RankedBars rows={rows.cities} emptyLabel={tt('noSwissData')} valueLabel={tt('unit')} />
                    </Card>
                  </div>

                  <Card title={tt('charts.heatmap')} icon={ClockIcon}>
                    {data.heatmap.length ? (
                      <WeekHourHeatmap data={data.heatmap} dayLabels={dayLabels} unitLabel={tt('unit')} />
                    ) : (
                      <EmptyState>{t('statistics.noData')}</EmptyState>
                    )}
                  </Card>
                </>
              )}
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
